import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import { getSupabaseClient } from '../../lib/supabase';
import { 
  ArrowDownLeft, 
  Search, 
  Plus, 
  IndianRupee, 
  Calendar, 
  User, 
  Printer, 
  RefreshCw, 
  CheckCircle2, 
  AlertCircle,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { ServiceVoucherPrintModal } from './ServiceVoucherPrintModal';

export function ServicesInwardPaymentView({ branchId }: { branchId?: string | null } = {}) {
  const { currentTenantId, activeBranchId } = useAuth();
  const supabase = getSupabaseClient();
  const effectiveBranchId = branchId || activeBranchId;

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<any[]>([]);
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Form states
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'upi' | 'card' | 'bank' | 'online' | 'cheque'>('cash');
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Voucher print state
  const [printVoucherId, setPrintVoucherId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!supabase || !currentTenantId) return;
    setLoading(true);

    try {
      // 1. Fetch customers with balance
      let custList: any[] = [];
      const { data: cData, error: cErr } = await supabase.rpc('get_customers_with_balance', {
        p_tenant_id: currentTenantId
      });
      if (!cErr && Array.isArray(cData)) {
        custList = cData;
      } else {
        const { data: rawCust } = await supabase
          .from('retail_customers')
          .select('id, customer_name, contact_no, balance, current_balance')
          .eq('tenant_id', currentTenantId);
        custList = rawCust || [];
      }
      setCustomers(custList);

      // 2. Fetch inward vouchers
      const { data: vData, error: vErr } = await supabase.rpc('list_service_vouchers', {
        p_tenant_id: currentTenantId,
        p_kind: 'inward',
        p_branch_id: effectiveBranchId || null,
        p_limit: 100
      });

      if (vErr) {
        console.error('list_service_vouchers error:', vErr);
        toast.error('Failed to load receipts: ' + vErr.message);
      } else if (Array.isArray(vData)) {
        setVouchers(vData);
      }
    } catch (err: any) {
      console.error('loadData error:', err);
      toast.error(err.message || 'Error loading inward payments');
    } finally {
      setLoading(false);
    }
  }, [supabase, currentTenantId, effectiveBranchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !currentTenantId) return;

    if (!selectedCustomerId) {
      toast.error('Please select a customer');
      return;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('service_receive_payment', {
        p_tenant_id: currentTenantId,
        p_branch_id: effectiveBranchId || null,
        p_customer_id: selectedCustomerId,
        p_amount: numAmount,
        p_payment_method: paymentMethod,
        p_payment_date: paymentDate || new Date().toISOString().slice(0, 10),
        p_note: note.trim() || null,
        p_invoice_id: null
      });

      if (error) {
        console.error('service_receive_payment error:', error);
        toast.error(error.message);
      } else {
        const receiptNo = data?.receipt_number || 'RCV-SUCCESS';
        toast.success(`Payment recorded! Receipt: ${receiptNo}`);
        setAmount('');
        setNote('');
        setSelectedCustomerId('');
        loadData();
        if (data?.id) {
          setPrintVoucherId(data.id);
        }
      }
    } catch (err: any) {
      console.error('Submit error:', err);
      toast.error(err.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCust = customers.find(c => c.id === selectedCustomerId);
  const custBalance = Number(selectedCust?.current_balance ?? selectedCust?.balance ?? 0);

  const filteredVouchers = vouchers.filter(v => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (v.receipt_number || '').toLowerCase().includes(q) ||
      (v.party || '').toLowerCase().includes(q) ||
      (v.method || '').toLowerCase().includes(q) ||
      (v.note || '').toLowerCase().includes(q)
    );
  });

  const totalReceived = vouchers.reduce((acc, v) => acc + (Number(v.amount) || 0), 0);

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <ArrowDownLeft className="text-emerald-600" size={24} />
            <span>Inward Payments (Customer Receipts)</span>
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Receive customer dues, advance settlements, and generate official print vouchers
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={loadData}
            disabled={loading}
            className="text-xs h-9 gap-1.5"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Record Payment Form */}
        <div className="lg:col-span-5 space-y-4">
          <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm rounded-2xl">
            <CardContent className="p-5 space-y-4">
              <h3 className="font-bold text-slate-900 dark:text-white text-sm pb-2 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
                <Plus size={16} className="text-emerald-600" />
                <span>Receive Customer Payment</span>
              </h3>

              <form onSubmit={handleSubmit} className="space-y-3.5 text-xs">
                {/* Customer Dropdown */}
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Select Customer <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={selectedCustomerId}
                    onChange={e => setSelectedCustomerId(e.target.value)}
                    className="w-full h-10 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white outline-none"
                    required
                  >
                    <option value="">-- Choose Customer --</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.customer_name || 'Customer'} {c.contact_no ? `(${c.contact_no})` : ''} - Due: ₹{Number(c.current_balance ?? c.balance ?? 0).toFixed(2)}
                      </option>
                    ))}
                  </select>

                  {selectedCust && (
                    <div className="mt-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 flex justify-between items-center text-xs">
                      <span className="text-slate-500">Current Outstanding Balance:</span>
                      <strong className={`font-mono text-sm ${custBalance > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600'}`}>
                        ₹{custBalance.toFixed(2)}
                      </strong>
                    </div>
                  )}
                </div>

                {/* Amount & Date */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Amount (₹) <span className="text-rose-500">*</span>
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={amount}
                      onChange={e => setAmount(e.target.value)}
                      placeholder="0.00"
                      required
                      className="h-10 text-xs font-mono font-bold"
                    />
                  </div>

                  <div>
                    <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                      Payment Date
                    </label>
                    <Input
                      type="date"
                      value={paymentDate}
                      onChange={e => setPaymentDate(e.target.value)}
                      className="h-10 text-xs font-mono"
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Payment Method
                  </label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {(['cash', 'upi', 'card', 'bank', 'online', 'cheque'] as const).map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setPaymentMethod(m)}
                        className={`py-2 px-1 rounded-xl text-[11px] font-bold uppercase transition-all border ${
                          paymentMethod === m
                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                            : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Note / Reference */}
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Notes / Ref (optional)
                  </label>
                  <Input
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="e.g. Cleared bill INV-1002 / Partial cash received"
                    className="h-9 text-xs"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !selectedCustomerId || !amount}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 rounded-xl shadow-md gap-1.5 mt-2"
                >
                  {submitting ? (
                    <RefreshCw size={14} className="animate-spin" />
                  ) : (
                    <CheckCircle2 size={15} />
                  )}
                  <span>Record & Issue Receipt</span>
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Vouchers History */}
        <div className="lg:col-span-7 space-y-4">
          {/* Stats Bar */}
          <div className="p-3.5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs flex items-center justify-between">
            <div>
              <span className="text-[11px] uppercase font-bold text-slate-400 block">Total Collections</span>
              <span className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
                ₹{totalReceived.toFixed(2)}
              </span>
            </div>
            <span className="text-xs text-slate-500">
              {vouchers.length} Vouchers
            </span>
          </div>

          {/* Search bar */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search receipt #, customer name, method..."
              className="pl-9 h-9 text-xs bg-white dark:bg-slate-900"
            />
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-xs">
            {loading ? (
              <div className="p-12 text-center text-slate-400 text-xs flex flex-col items-center justify-center gap-2">
                <RefreshCw size={20} className="animate-spin text-primary" />
                <span>Loading payment records...</span>
              </div>
            ) : filteredVouchers.length === 0 ? (
              <div className="p-10 text-center text-slate-400 space-y-2">
                <FileText size={28} className="mx-auto text-slate-300 dark:text-slate-600" />
                <p className="text-xs font-semibold">No inward payments found</p>
                <p className="text-[11px] text-slate-400">Record a customer receipt from the left form.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase font-bold text-slate-500 border-b border-slate-100 dark:border-slate-800">
                    <tr>
                      <th className="p-3">Receipt #</th>
                      <th className="p-3">Date</th>
                      <th className="p-3">Customer</th>
                      <th className="p-3">Mode</th>
                      <th className="p-3 text-right">Amount</th>
                      <th className="p-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredVouchers.map(v => (
                      <tr key={v.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/40 transition-colors">
                        <td className="p-3 font-mono font-bold text-slate-900 dark:text-white">
                          {v.receipt_number || 'RCV-#'}
                        </td>
                        <td className="p-3 text-slate-500 whitespace-nowrap">
                          {v.date ? new Date(v.date).toLocaleDateString('en-IN') : '-'}
                        </td>
                        <td className="p-3 font-medium text-slate-800 dark:text-slate-200">
                          {v.party || 'Customer'}
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                            {v.method || 'cash'}
                          </span>
                        </td>
                        <td className="p-3 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                          ₹{Number(v.amount || 0).toFixed(2)}
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setPrintVoucherId(v.id)}
                            className="h-7 px-2.5 text-[11px] gap-1 font-semibold text-slate-700 dark:text-slate-300"
                          >
                            <Printer size={12} />
                            <span>Print</span>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Voucher Print Modal */}
      {printVoucherId && (
        <ServiceVoucherPrintModal
          kind="inward"
          voucherId={printVoucherId}
          isOpen={!!printVoucherId}
          onClose={() => setPrintVoucherId(null)}
        />
      )}
    </div>
  );
}

export default ServicesInwardPaymentView;
